import { useCallback, useLayoutEffect, useRef } from 'react';

const DEFAULT_OPEN_DURATION_MS = 600;
const DEFAULT_CLOSE_DURATION_MS = 250;
// Opacity finishes early in the transform so the modal is fully solid for most
// of the growth — avoids translucent look at mid/large scales.
const OPACITY_DURATION_RATIO = 0.4;
// Gentle decelerating curve — natural grow-out-of-launcher motion.
const OPEN_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
// Accelerating curve — starts slow, ends fast. Matches "quick collapse into the launcher" feel.
const CLOSE_EASING = 'cubic-bezier(0.55, 0.055, 0.675, 0.19)';

const isReducedMotion = () => (
  typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
);

/**
 * useGenieTransition — FLIP (First, Last, Invert, Play) animation between
 * a launcher rect and a target element's rect.
 *
 *   - **First**   — measure the starting rect (launcher).
 *   - **Last**    — measure the final rect (target's actual DOM position).
 *   - **Invert**  — apply an inverse transform so target visually renders at First.
 *   - **Play**    — transition the transform back to identity; target visually moves First → Last.
 *
 * Animates transform + opacity on the target so the DOM never actually moves
 * during the transition. This helps performance because only compositor-cheap
 * properties change (i.e. transform, opacity) — and **not** layout-affecting
 * properties (e.g. width, height, top, margin, etc.).
 *
 * The target element must be the actual visible box you want to animate.
 * Applying transform to a wrapper of a `position: fixed` child creates a new
 * containing block and breaks the child's viewport-relative positioning —
 * point `getTargetElement` at the fixed-position element directly.
 *
 * Enter (expand — modal grows from launcher rect out to full size, on mount):
 * reads First (launcher rect via getOriginRect) and Last (target's current DOM
 * rect). Applies inverse transform so the target visually renders AT the
 * launcher position/size, then transitions the transform back to identity.
 *
 * Close (collapse — modal shrinks back into launcher rect, via handleClose):
 * applies the forward transform toward the launcher rect (re-read live), waits
 * for `transitionend`, then invokes onCloseRequest. Guarantees the modal is
 * visually collapsed into the launcher before it actually unmounts.
 *
 * Skips animation entirely if either callback returns null or user prefers
 * reduced motion.
 *
 * @param {Function} getOriginRect — callback returning origin (launcher)
 *   DOMRect at call time. Stable identity required (memoize in caller).
 * @param {Function} getTargetElement — callback returning the DOM element to
 *   animate. Stable identity required. Called at enter + close time (live lookup,
 *   handles React remounts and library-owned DOM that we can't ref directly).
 * @param {Function} onCloseRequest — called after close animation ends to
 *   actually close/unmount the modal (e.g. dialogManager.closeDialog).
 * @param {number} [openDurationMs=600] ms — enter animation duration.
 * @param {number} [closeDurationMs=250] ms — close animation duration.
 *
 * @returns {{ handleClose }}
 */
export const useGenieTransition = ({
  getOriginRect,
  getTargetElement,
  onCloseRequest,
  openDurationMs = DEFAULT_OPEN_DURATION_MS,
  closeDurationMs = DEFAULT_CLOSE_DURATION_MS
} = {}) => {
  const hasEnteredRef = useRef(false);

  // Enter (expand) FLIP — runs once on mount (before modal is visible on screen).
  useLayoutEffect(() => {
    if (hasEnteredRef.current) {
      return undefined;
    }
    hasEnteredRef.current = true;

    const target = getTargetElement?.();
    const originRect = getOriginRect?.();
    if (!target || !originRect || isReducedMotion()) {
      return undefined;
    }

    // FLIP invert: translate + scale so the target visually renders AT the
    // launcher rect (icon-sized, at icon position). During the play phase the
    // transform interpolates to identity — the modal both moves AND grows
    // continuously from icon to final rect, preserving visual connection to
    // the launcher throughout the animation.
    const targetRect = target.getBoundingClientRect();
    const translateX = originRect.left - targetRect.left;
    const translateY = originRect.top - targetRect.top;
    const scaleX = originRect.width / targetRect.width;
    const scaleY = originRect.height / targetRect.height;
    if (!Number.isFinite(translateX) || !Number.isFinite(translateY)
      || !Number.isFinite(scaleX) || !Number.isFinite(scaleY)) {
      return undefined;
    }

    target.style.transformOrigin = 'top left';
    target.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`;
    target.style.opacity = '0';
    target.style.transition = 'none';

    // Force layout to flush the invert; without this, the browser batches invert + play and skips the transition.
    target.getBoundingClientRect();

    // Play: enable transition + return to identity in next frame.
    const opacityDurationMs = Math.round(openDurationMs * OPACITY_DURATION_RATIO);
    const rafId = requestAnimationFrame(() => {
      target.style.transition = `transform ${openDurationMs}ms ${OPEN_EASING}, opacity ${opacityDurationMs}ms ease-out`;
      target.style.transform = '';
      target.style.opacity = '';
    });

    // transitionend fires once per transitioned property — filter on transform so cleanup only runs once.
    const onEnd = (event) => {
      if (event.target !== target || event.propertyName !== 'transform') {
        return;
      }
      target.style.transition = '';
      target.style.transformOrigin = '';
      target.removeEventListener('transitionend', onEnd);
    };
    target.addEventListener('transitionend', onEnd);

    return () => {
      cancelAnimationFrame(rafId);
      target.removeEventListener('transitionend', onEnd);
    };
  }, [getOriginRect, getTargetElement, openDurationMs]);

  // Close (collapse) FLIP — runs when caller invokes handleClose.
  const handleClose = useCallback(async () => {
    const target = getTargetElement?.();
    const originRect = getOriginRect?.();
    if (!target || !originRect || isReducedMotion()) {
      onCloseRequest?.();
      return;
    }

    // FLIP close: mirror of open. Transition from identity toward the
    // translate+scale that lands the target visually AT the launcher rect,
    // so the modal both moves AND shrinks continuously back into the icon.
    const targetRect = target.getBoundingClientRect();
    const translateX = originRect.left - targetRect.left;
    const translateY = originRect.top - targetRect.top;
    const scaleX = originRect.width / targetRect.width;
    const scaleY = originRect.height / targetRect.height;
    if (!Number.isFinite(translateX) || !Number.isFinite(translateY)
      || !Number.isFinite(scaleX) || !Number.isFinite(scaleY)) {
      onCloseRequest?.();
      return;
    }

    target.style.transformOrigin = 'top left';
    target.style.transition = `transform ${closeDurationMs}ms ${CLOSE_EASING}, opacity ${closeDurationMs}ms ease-in`;
    target.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`;
    target.style.opacity = '0';

    await new Promise((resolve) => {
      // transitionend fires once per transitioned property — filter on transform so resolve only runs once.
      const onEnd = (event) => {
        if (event.target !== target || event.propertyName !== 'transform') {
          return;
        }
        target.removeEventListener('transitionend', onEnd);
        resolve();
      };
      target.addEventListener('transitionend', onEnd);
    });

    onCloseRequest?.();
  }, [getOriginRect, getTargetElement, closeDurationMs, onCloseRequest]);

  return { handleClose };
};
